'use client';

import { useState, useMemo, useEffect } from 'react';

// === ТИПЫ И ДАННЫЕ ===

export type MediaType = 'video' | 'photo' | 'press' | 'podcast' | 'interview';

export interface MediaItem {
  id: string;
  type: MediaType;
  title: string;
  date: string;
  views: number;
  tags?: string[];
  cover: string;
  url: string;
  source: string;
  duration?: string;
  desc: string;
}

const MEDIA: MediaItem[] = [
  {
    id: 'm1', type: 'video', title: 'Презентация INTERIM', date: '2025-08-10', views: 1200,
    tags: ['Компания', 'Презентация'], cover: 'https://placehold.co/800x480?text=Video+1',
    url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', source: 'YouTube', duration: '02:15',
    desc: 'Короткий ролик о наших услугах и подходе.'
  },
  {
    id: 'm2', type: 'video', title: 'Кейс: массовый подбор', date: '2025-08-07', views: 980,
    tags: ['Кейс', 'RPO'], cover: 'https://placehold.co/800x480?text=Video+Case',
    url: 'https://example.com/case-video.mp4', source: 'MP4', duration: '00:30',
    desc: 'Кейс по маркетплейсу: 180 закрытий/кв.'
  },
  {
    id: 'm3', type: 'photo', title: 'Команда INTERIM', date: '2025-07-20', views: 540,
    tags: ['Команда', 'Офис'], cover: 'https://placehold.co/800x480?text=Team+Photo',
    url: 'https://placehold.co/1600x900?text=Team+Photo+HD', source: 'Gallery',
    desc: 'Групповое фото core-команды.'
  },
  {
    id: 'm4', type: 'press', title: 'В Forbes: автоматизация рекрутинга', date: '2025-07-30', views: 1300,
    tags: ['СМИ', 'Аналитика'], cover: 'https://placehold.co/800x480?text=Press+Forbes',
    url: 'https://example.com/forbes-article', source: 'Forbes',
    desc: 'Интервью о трендах и метриках подбора.'
  },
  {
    id: 'm5', type: 'podcast', title: 'Подкаст: KPI и SLA в RPO', date: '2025-08-01', views: 450,
    tags: ['Подкаст', 'RPO'], cover: 'https://placehold.co/800x480?text=Podcast',
    url: 'https://www.youtube.com/watch?v=S-sJvVv3KqA', source: 'YouTube', duration: '15:00',
    desc: 'Обсуждаем KPI, SLA и воронку кандидатов.'
  },
  {
    id: 'm6', type: 'interview', title: 'Интервью для РБК', date: '2025-06-18', views: 670,
    tags: ['СМИ', 'Интервью'], cover: 'https://placehold.co/800x480?text=Interview+RBK',
    url: 'https://example.com/rbk-interview', source: 'РБК',
    desc: 'Про рынок e-commerce и массовый найм.'
  },
  {
    id: 'm7', type: 'photo', title: 'Бек-стейдж съёмки', date: '2025-08-12', views: 310,
    tags: ['Бэкстейдж'], cover: 'https://placehold.co/800x480?text=Behind+the+Scenes',
    url: 'https://placehold.co/1600x900?text=Behind+the+Scenes+HD', source: 'Gallery',
    desc: 'За кадром: процесс работы и съёмок.'
  },
  {
    id: 'm8', type: 'video', title: 'Аутстаффинг: как это работает', date: '2025-07-05', views: 820,
    tags: ['Аутстаффинг', 'Разбор'], cover: 'https://placehold.co/800x480?text=Video+Outstaff',
    url: 'https://www.youtube.com/watch?v=A-t_wz8-xN8', source: 'YouTube', duration: '03:40',
    desc: 'Объясняем модель аутстаффинга простым языком.'
  },
];

const fmtDate = (s: string) => new Date(s).toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' });
const badgeClass = (type: MediaType) => {
  switch (type) {
    case 'video': return 'badge-vid';
    case 'photo': return 'badge-ph';
    case 'press': return 'badge-pr';
    case 'podcast': return 'badge-pod';
    case 'interview': return 'badge-int';
    default: return '';
  }
};
function isYouTube(u: string): boolean { return /youtube\.com|youtu\.be/.test(u); }
function ytEmbed(u: string): string {
  try {
    const idMatch = u.match(/(?:v=|\/embed\/|youtu\.be\/|v\/|t=)([^&\n?#]+)/);
    const id = idMatch ? idMatch[1] : '';
    return `https://www.youtube.com/embed/${id}?rel=0`;
  } catch { return ''; }
}

// === КОМПОНЕНТЫ ===

interface MediaCardProps {
  item: MediaItem;
  onOpenModal: (item: MediaItem) => void;
}

const MediaCard = ({ item, onOpenModal }: MediaCardProps) => {
  return (
    <article className="card rounded-2xl overflow-hidden flex flex-col">
      <img
        loading="lazy"
        src={item.cover}
        alt=""
        // h-44 для лучшего вида на мобильных
        className="w-full h-44 object-cover"
        onError={(e) => (e.currentTarget.src = 'https://placehold.co/800x480?text=Media')}
      />
      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg sm:text-xl font-semibold">{item.title}</h3>
          <span className={`badge ${badgeClass(item.type)} text-xs flex-shrink-0`}>{item.type}</span>
        </div>
        <div className="mt-1 text-xs flex flex-wrap gap-2">
          <span className="tag">{fmtDate(item.date)}</span>
          <span className="tag">Источник: {item.source || '—'}</span>
          {item.duration && <span className="tag">Длительность: {item.duration}</span>}
        </div>
        <p className="text-sm text-gray-200 mt-3 line-clamp-3">{item.desc || ''}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {(item.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="tag">{t}</span>
          ))}
        </div>

        {/* ИЗМЕНЕНИЕ: flex-wrap и btn-sm для компактности и переноса на мобильных */}
        <div className="mt-auto flex flex-wrap items-center gap-2 sm:gap-3 pt-4 border-t border-white/10">
          <button
            className="btn btn-ghost btn-sm text-sm"
            onClick={() =>
              navigator.share?.({
                title: item.title,
                text: item.desc,
                url: window.location.origin + '#media-' + item.id,
              })
            }
          >
            Поделиться
          </button>
          <button
            className="btn btn-ghost btn-sm text-sm"
            onClick={() =>
              navigator.clipboard
                .writeText(window.location.origin + '#media-' + item.id)
                .then(() => alert('Ссылка скопирована.'))
            }
          >
            Ссылка
          </button>
          <button
            className="btn btn-accent btn-sm text-sm"
            onClick={() => onOpenModal(item)}
          >
            Открыть
          </button>
        </div>
      </div>
    </article>
  );
};


interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem: MediaItem | null;
}

const Modal = ({ isOpen, onClose, mediaItem }: ModalProps) => {
  if (!isOpen || !mediaItem) return null;

  const renderContent = () => {
    if (mediaItem.type === 'video') {
      const isYT = isYouTube(mediaItem.url);
      const embedUrl = isYT ? ytEmbed(mediaItem.url) : mediaItem.url;
      return (
        <div className="player aspect-video">
          {isYT ? (
            <iframe
              src={embedUrl}
              allowFullScreen
              loading="lazy"
              className="w-full h-full rounded-xl"
            ></iframe>
          ) : (
            <video
              src={embedUrl}
              controls
              playsInline
              className="w-full h-full rounded-xl"
            ></video>
          )}
        </div>
      );
    }
    if (mediaItem.type === 'photo') {
      return (
        <img
          src={mediaItem.url || mediaItem.cover}
          alt={mediaItem.title}
          // ИЗМЕНЕНИЕ: max-h для мобильных
          className="rounded-xl w-full object-contain max-h-[75vh]"
        />
      );
    }
    return (
      <div className="space-y-3">
        <img src={mediaItem.cover} className="rounded-xl w-full h-56 object-cover" alt="" />
        <p className="text-sm text-gray-200">{mediaItem.desc || ''}</p>
        <a
          href={mediaItem.url}
          target="_blank"
          rel="noopener"
          className="btn btn-accent inline-block text-sm"
        >
          Открыть источник
        </a>
      </div>
    );
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 ${isOpen ? 'animate-fadeIn' : 'hidden'}`}
      onClick={onClose}
    >
      <div
        // ИЗМЕНЕНИЕ: max-w-lg для мобильных, max-h-[90vh] и overflow-y-auto для контента
        className="bg-blue-900 text-white rounded-2xl p-4 sm:p-6 max-w-lg md:max-w-4xl w-full relative shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          id="close"
          className="absolute right-3 top-3 px-2 py-1 rounded-lg btn-ghost text-white hover:bg-white/20 text-xl"
          aria-label="Закрыть"
          onClick={onClose}
        >
          ✕
        </button>
        <article id={`media-${mediaItem.id}`}>
          <h2 className="text-xl sm:text-2xl font-bold mb-3 pr-8">{mediaItem.title}</h2>
          <div className="text-xs flex flex-wrap gap-2 mb-4">
            <span className="tag bg-white/20">{fmtDate(mediaItem.date)}</span>
            <span className="tag bg-white/20">Тип: {mediaItem.type}</span>
            <span className="tag bg-white/20">Источник: {mediaItem.source || '—'}</span>
            {mediaItem.duration && (
              <span className="tag bg-white/20">Длительность: {mediaItem.duration}</span>
            )}
          </div>
          {renderContent()}
          <div className="mt-4 flex gap-2">
            <button
              className="btn btn-ghost text-sm text-white hover:bg-white/20"
              onClick={() =>
                navigator.clipboard
                  .writeText(window.location.origin + '#media-' + mediaItem.id)
                  .then(() => alert('Ссылка скопирована.'))
              }
            >
              Скопировать ссылку
            </button>
          </div>
        </article>
      </div>

      <style jsx>{`
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};


// === ОСНОВНОЙ КОМПОНЕНТ СТРАНИЦЫ ===

export default function MediaPage() {
  const [query, setQuery] = useState('');
  const [selectedType, setSelectedType] = useState<MediaType | ''>('');
  const [selectedTag, setSelectedTag] = useState('');
  const [yearRange, setYearRange] = useState<[number, number]>([2025, 2026]);
  const [sortBy, setSortBy] = useState('date');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  const allTags = useMemo(() => {
    const tags = MEDIA.flatMap(m => m.tags || []);
    return [...new Set(tags)].sort((a, b) => a.localeCompare(b, 'ru'));
  }, []);

  const filteredAndSortedMedia = useMemo(() => {
    let items = MEDIA.filter(m => {
      const hay = (m.title + ' ' + (m.desc || '') + ' ' + (m.tags || []).join(' ')).toLowerCase();
      const year = new Date(m.date).getFullYear();
      return (
        (!query || hay.includes(query.toLowerCase())) &&
        (!selectedType || m.type === selectedType) &&
        (!selectedTag || (m.tags || []).includes(selectedTag)) &&
        (year >= yearRange[0] && year <= yearRange[1])
      );
    });

    if (sortBy === 'date') items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    if (sortBy === 'popular') items.sort((a, b) => (b.views || 0) - (a.views || 0));
    if (sortBy === 'title') items.sort((a, b) => a.title.localeCompare(b.title, 'ru'));

    return items;
  }, [query, selectedType, selectedTag, yearRange, sortBy]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, selectedType, selectedTag, yearRange, sortBy]);

  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredAndSortedMedia.slice(start, start + perPage);
  }, [filteredAndSortedMedia, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedMedia.length / perPage);

  const handleOpenModal = (item: MediaItem) => {
    setSelectedMedia(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <a href="#gallery" className="chip px-3 py-1 rounded-full hover:bg-white/25">Галерея</a>
            <a href="#filters" className="chip px-3 py-1 rounded-full hover:bg-white/25">Фильтры</a>
            <a href="#presskit" className="chip px-3 py-1 rounded-full hover:bg-white/25">Press Kit</a>
            <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">FAQ</a>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Медиа-центр INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Фото и видео компании, публикации в СМИ, подкасты и интервью. Готовые материалы для прессы и партнёров.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#gallery" className="btn btn-ghost">Открыть галерею</a>
            <a href="#presskit" className="btn btn-accent">Скачать Press Kit</a>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="filters" aria-label="Фильтры медиа" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Подбор по параметрам</h2>
          {/* ИЗМЕНЕНИЕ: grid-cols-1 на мобильных, md:grid-cols-3, lg:grid-cols-6 на десктопах */}
          <div className="grid gap-3 grid-cols-1 md:grid-cols-3 lg:grid-cols-6">
            <input
              id="q"
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-3 lg:col-span-2"
              placeholder="Поиск по заголовку/описанию…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              id="type"
              className="rounded-xl px-4 py-3 text-gray-900"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as MediaType | '')}
            >
              <option value="">Тип: все</option>
              <option value="video">Видео</option>
              <option value="photo">Фото</option>
              <option value="press">Публикации</option>
              <option value="podcast">Подкасты</option>
              <option value="interview">Интервью</option>
            </select>
            <select
              id="tag"
              className="rounded-xl px-4 py-3 text-gray-900"
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
            >
              <option value="">Тег: все</option>
              {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
            </select>
            <div className="rounded-xl px-4 py-3 card flex items-center gap-2 lg:col-span-2 flex-wrap sm:flex-nowrap">
              <label className="text-sm text-gray-200 flex-shrink-0">Годы</label>
              <input
                id="y1"
                type="range"
                min="2025"
                max="2026"
                value={yearRange[0]}
                className="flex-1"
                onChange={(e) => setYearRange([+e.target.value, yearRange[1] > +e.target.value ? yearRange[1] : +e.target.value])}
              />
              <span id="y1v" className="text-sm flex-shrink-0">{yearRange[0]}</span>
              <input
                id="y2"
                type="range"
                min="2025"
                max="2026"
                value={yearRange[1]}
                className="flex-1"
                onChange={(e) => setYearRange([yearRange[0] < +e.target.value ? yearRange[0] : +e.target.value, +e.target.value])}
              />
              <span id="y2v" className="text-sm flex-shrink-0">{yearRange[1]}</span>
            </div>
            <select
              id="sort"
              className="rounded-xl px-4 py-3 text-gray-900 min-w-full md:min-w-0 md:col-span-3 lg:col-span-1 whitespace-nowrap"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Сортировать: по дате ↓</option>
              <option value="popular">по популярности</option>
              <option value="title">по названию</option>
            </select>
          </div>
        </section>

        <section id="gallery" aria-label="Галерея медиа" className="scroll-mt-32">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <h2 className="text-3xl md:text-4xl font-bold">Галерея</h2>
            <span id="count" className="text-sm text-gray-300">Найдено материалов: {filteredAndSortedMedia.length}</span>
          </div>
          {/* ИЗМЕНЕНИЕ: grid-cols-1 на мобильных, md:grid-cols-2 на планшетах, lg:grid-cols-3 на десктопах */}
          <div id="grid" className="mt-6 grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {paginatedMedia.length > 0 ? (
              paginatedMedia.map(item => (
                <MediaCard key={item.id} item={item} onOpenModal={handleOpenModal} />
              ))
            ) : (
              <p id="empty" className="text-gray-300 sm:col-span-2 lg:col-span-3">Ничего не найдено — измените фильтры.</p>
            )}
          </div>
          <div id="pager" className="mt-6 flex flex-wrap items-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                className={`px-3 py-2 rounded-lg ${page === currentPage ? 'btn-accent' : 'btn-ghost'}`}
                onClick={() => {
                  setCurrentPage(page);
                  // ФИКС: При смене страницы прокручиваем к элементу с ID 'gallery',
                  // чтобы пользователь видел новый контент, особенно на мобильных.
                  document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {page}
              </button>
            ))}
          </div>
        </section>

        <section id="presskit" className="simple-card scroll-mt-32">
          <h3 className="text-2xl font-bold">Press Kit</h3>
          <p className="text-sm text-gray-200 mt-1">
            Логотипы, цветовые коды, фото команды, примеры использования бренда.
          </p>

          {/* ИЗМЕНЕНИЕ: grid-cols-1 на мобильных, md:grid-cols-3 на планшетах */}
          <div className="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <article className="card rounded-xl p-4" style={{ transform: 'none', transition: 'none' }}>
              <h4 className="font-semibold">Логотипы</h4>
              <p className="text-sm text-gray-300">SVG, PNG (тёмная/светлая версии).</p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <a className="btn btn-ghost btn-sm text-sm" href="#" download style={{ transform: 'none', transition: 'none' }}>
                  Скачать ZIP
                </a>
                <a className="btn btn-accent btn-sm text-sm" href="#" style={{ transform: 'none', transition: 'none' }}>
                  Просмотр
                </a>
              </div>
            </article>

            <article className="card rounded-xl p-4" style={{ transform: 'none', transition: 'none' }}>
              <h4 className="font-semibold">Гайд по бренду</h4>
              <p className="text-sm text-gray-300">
                Правила использования логотипа, поля, фон.
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <a className="btn btn-ghost btn-sm text-sm" href="#" download style={{ transform: 'none', transition: 'none' }}>
                  PDF
                </a>
                <a className="btn btn-accent btn-sm text-sm" href="/contacts" style={{ transform: 'none', transition: 'none' }}>
                  Запросить редакцию
                </a>
              </div>
            </article>

            <article className="card rounded-xl p-4" style={{ transform: 'none', transition: 'none' }}>
              <h4 className="font-semibold">Фото и фоновые изображения</h4>
              <p className="text-sm text-gray-300">
                Подборка для публикаций и новостей.
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                <a className="btn btn-ghost btn-sm text-sm" href="#" download style={{ transform: 'none', transition: 'none' }}>
                  Скачать
                </a>
                <a className="btn btn-accent btn-sm text-sm" href="#gallery" style={{ transform: 'none', transition: 'none' }}>
                  Открыть галерею
                </a>
              </div>
            </article>
          </div>
        </section>

        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
          <div className="space-y-3">
            <details className="simple-card"><summary className="cursor-pointer font-semibold">Можно ли встраивать наши видео?</summary><p className="mt-2 text-sm text-gray-200">Да — у каждой карточки есть кнопка «Копировать ссылку».</p></details>
            <details className="simple-card"><summary className="cursor-pointer font-semibold">Где взять оригиналы фото?</summary><p className="mt-2 text-sm text-gray-200">В разделе Press Kit — «Фото и фоновые изображения».</p></details>
          </div>
        </section>
      </main>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} mediaItem={selectedMedia} />
    </div>
  );
}